import React, { useContext, useEffect, useRef, useState } from "react";import Footer from "./Footer";
import { languageData } from "../constants/languageData";
import Head from "../containers/Head";
import Loader from "./Loader/Loader";
import RenderSvg from "./RenderSvg";
import { trackAction } from "../utils/hyperdx";

function Expired({
  stepperFunc,
  isSessionExpired,
  isJourneyExpired,
}) {
  const config = { branding: { accentColor: "" } };
  // Guarded: sessionStorage may hold the literal "undefined" (branding-less flow),
  // which JSON.parse throws on — that must not white-screen the terminal page.
  let branding = null;
  try {
    branding = JSON.parse(sessionStorage.getItem("branding"));
  } catch (error) {
    branding = null;
  }
  config.branding.accentColor = branding?.accentColor;
  
  // Get values from sessionStorage - same pattern as failure case
  const storedStatusOption = sessionStorage.getItem("showStatusScreenBeforeRedirection");
  const failureRedirectUrl = sessionStorage.getItem("failureRedirectUrl");
  const language = sessionStorage.getItem("language") ?? "en";
  const redirectTime = sessionStorage.getItem("redirectTime");

  // State management - identical to failure case
  const [showStatusScreenBeforeRedirection] = useState(
    storedStatusOption ? storedStatusOption === "true" : config?.userSettings?.showStatusScreenBeforeRedirection ?? true
  );
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(-1);
  const hasMarkedComplete = useRef(false);
  const defaultRedirectTime = 1;

  // Same logic flow as failure case
  const shouldRedirect = !!failureRedirectUrl;
  const shouldShowUI = !shouldRedirect || showStatusScreenBeforeRedirection;

  // Determine error texts - preserving original logic
  const getErrorTexts = () => {
    if (isJourneyExpired) {
      return languageData[language]?.journeyExpiredError ?? languageData["en"]?.journeyExpiredError;
    }
    if (isSessionExpired) {
      return languageData[language]?.sessionExpiredError ?? languageData["en"]?.sessionExpiredError;
    }
    return languageData[language]?.sessionExpiredError ?? languageData["en"]?.sessionExpiredError;
  };

  const errorHandlerTexts = getErrorTexts();
  const redirectText = languageData[language]?.redirect ?? languageData["en"]?.redirect;

  // Terminal outcome: link expired or session timed out.
  useEffect(() => {
    trackAction("journey:outcome", {
      outcome: isJourneyExpired ? "link_expired" : "session_timeout",
    });
  }, []);

  // Initialization effect - same pattern
  useEffect(() => {
    const handleExpiration = async () => {
      if (!hasMarkedComplete.current) {
        hasMarkedComplete.current = true;
        window.parent.postMessage(isJourneyExpired ? "Link Expired" : "Session Timed Out", "*");
        
        setLoading(!shouldShowUI);
        
        if (shouldRedirect) {
          const initialCount = redirectTime ? parseInt(redirectTime) : defaultRedirectTime;
          setCount(initialCount);
        }
      }
    };

    handleExpiration();
  }, []);

  // Countdown effect - identical structure
  useEffect(() => {
    if (count < 0) return;
    
    const timer = setTimeout(() => {
      if (count > 0) {
        setCount(count - 1);
      } else if (shouldRedirect) {
        clearSessionStorage();
        window.location.href = failureRedirectUrl;
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [count, shouldRedirect, failureRedirectUrl]);

  // Same session cleanup function
  const clearSessionStorage = () => {
    [
      "sessionId", "jId", "currentStep", "companyLogo", "redirectTime",
      "language", "document", "ipAddress", "selectedCountry",
      "failureRedirectUrl", "successRedirectUrl", "documentFailureReason"
    ].forEach(item => sessionStorage.removeItem(item));
  };

  // Identical loading/UI logic
  if (loading || !shouldShowUI) {
    return <Loader />;
  }

  return (
    <>
      <Head
        style={{ zIndex: "100" }}
        showMobileFlow={false}
        showHelp={false}
        stepperFunc={stepperFunc}
        showLanguages={false}
      />
      <div className="parent-container">
        <div className="main">
          <div className="left-section">
            <RenderSvg
              config={config}
              id={"error"}
              svgType={"error"}
              style={{ marginTop: "3rem" }}
            />
          </div>
          <div
            className="right-section md:items-start items-center"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 24,
              fontFamily: "Inter, sans-serif",

            }}
          >
            <div
              className="flex flex-col"
              style={{ gap: "16px", marginBottom: "auto" }}
            >
              <div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
                {errorHandlerTexts.heading}
              </div>
              <div className="text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left">
                {errorHandlerTexts.subHeading1}
              </div>
              {failureRedirectUrl && (
                <div className="text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left">
                  {count > 0 ? (
                    <>
                      {redirectText} {count}...{" "}
                      {languageData[language]?.redirectCta?.or ??
                        languageData["en"]?.redirectCta?.or}{" "}
                      <span
                        onClick={() => {
                          clearSessionStorage();
                          window.location.href = failureRedirectUrl;
                        }}
                        style={{
                          color: "#1658aa",
                          cursor: "pointer",
                        }}
                      >
                        {languageData[language]?.redirectCta?.clickHere ??
                          languageData["en"]?.redirectCta?.clickHere}
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        onClick={() => {
                          clearSessionStorage();
                          window.location.href = failureRedirectUrl;
                        }}
                        style={{
                          color: "#1658aa",
                          cursor: "pointer",
                        }}
                      >
                        {languageData[language]?.redirectCta?.clickHereStart ??
                          languageData["en"]?.redirectCta?.clickHereStart}
                      </span>
                      {" "}
                      {languageData[language]?.redirectCta?.toContinue ??
                        languageData["en"]?.redirectCta?.toContinue}
                    </>
                  )}
                </div>
              )}
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default Expired;
